import { useEffect, useRef, useState } from "react";
import { ListBigNumber, SupportBoard } from "./components";
import { BigNumberSupport } from "./components/ListBigNumber";
import { LoadMoreButton, SelectOption } from "@rio-cloud/rio-uikit";
import { useIntl } from "react-intl";
import { fetchOccurrences, fetchOccurrencesBigNumber, fetchSupportRegionals } from "../../api/tas";
import { IOccurrence } from "./types";

interface Props {
    assetIds: string[]
}

const SupportPage = ({ assetIds }: Props) => {
    const [type, setType] = useState<BigNumberSupport>('all');
    const [data, setData] = useState<IOccurrence[]>([]);
    const [supportType, setSupportType] = useState<undefined | string>(undefined);
    const [search, setSearch] = useState('');
    const [dn, setDn] = useState('');
    const [responsible, setResponsible] = useState('');
    const [total, setTotal] = useState(0);
    const [size, setSize] = useState(30);
    const [isLoading, setIsLoading] = useState(true);
    const [optionsRegionals, setOptionsRegionals] = useState<SelectOption[]>([]);
    const [regional, setRegional] = useState('');
    const [bigNumber, setBigNumber] = useState({
        OPEN: 0,
        IN_ANALYSIS: 0,
        ANSWERED: 0,
        FINISHED: 0,
    });

    const intl = useIntl();

    const fetchList = async () => {
        try {
            const res = await fetchOccurrences({
                status: type === 'all' ? null : type,
                page: 0,
                size: size,
                searchTerm: search,
                supportType: supportType === 'all' ? null : supportType,
                dn: dn,
                responsible: responsible,
                regional: regional === 'all' ? null : regional,
            });
            setData(res.content);
            setTotal(res.totalElements);

        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    const fetchBigNumberData = async () => {
        const res = await fetchOccurrencesBigNumber({
            searchTerm: search,
            supportType: supportType === 'all' ? null : supportType,
            dn: dn,
            responsible: responsible,
            regional: regional === 'all' ? null : regional,
        });
        setBigNumber(res);
    }


    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleSearch = (text: string) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setSearch(text);
        }, 500);
    };

    const handleDn = (text: string) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setDn(text);
        }, 500);
    };

    const handleResponsible = (text: string) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setResponsible(text);
        }, 500);
    };

    const handleSupportType = (sub: string) => {
        setSupportType(sub);
    };

    const handleRegional = (rg: string) => {
        setRegional(rg);
    };

    const fetchRegionals = async () => {
        const res = await fetchSupportRegionals();
        if (res?.content) {
            const all = {
                id: 'all',
                label: 'Todos'
            };

            const map = res?.content?.map((res: { id: number, name: string }) => {
                return {
                    id: res.id.toString(),
                    label: res.name,
                }
            })
            setOptionsRegionals([...map, all]);
        }
    };

    const onLoadMore = () => {
        const nextSize = size + 30;

        setSize(nextSize > total ? total : nextSize);
    }

    useEffect(() => {
        fetchRegionals()
    }, []);

    useEffect(() => {
        fetchList()
    }, [type, bigNumber, search, supportType, dn, responsible, size, regional]);

    useEffect(() => {
        fetchBigNumberData();
    }, [search, supportType, dn, responsible, regional]);

    return (
        <div className="margin-y-20">
            <ListBigNumber
                selected={type}
                analyze={bigNumber.IN_ANALYSIS}
                dealer={bigNumber.ANSWERED}
                finished={bigNumber.FINISHED}
                pending={bigNumber.OPEN}
                handleType={(type) => setType(type)}
            />
            <SupportBoard
                isLoading={isLoading}
                data={data}
                optionsRegionals={optionsRegionals}
                handleSearch={handleSearch}
                handleSupportType={handleSupportType}
                handleDn={handleDn}
                handleResponsible={handleResponsible}
                handleRegional={handleRegional}
            />
            <LoadMoreButton
                loaded={data?.length < 30 ? data.length : size}
                total={total}
                onLoadMore={onLoadMore}
                loadMoreMessage={intl.formatMessage({ id: "intl-msg:monitoring.loadMore" })}
                noMoreMessage={intl.formatMessage({ id: "intl-msg:monitoring.allLoaded" })}
            />
        </div>
    )
};

export default SupportPage